/**
 * The designer.
 *
 * This is the screen where Cloud is a host application of the SDK and nothing more:
 * `createReportDesigner` is mounted with the user's own data source, their data and
 * a theme, and everything a template can be is already there. Cloud's whole
 * contribution is *which* options it passes.
 *
 * Four of those options are the product decisions, and each is worth its reason.
 *
 * **The feature flags hide the developer affordances.** No free-form expressions,
 * no JSON view, no custom blocks, no library panel — and page settings and document
 * styles are behind a switch rather than in the way. A Cloud user has nowhere to
 * write an expression and no reason to read a template; putting either in front of
 * them is how a document tool starts feeling like a development environment.
 *
 * **The canvas measures with the engine.** `createFormeMeasurer` rather than the
 * DOM measurer, so a page breaks on screen exactly where it breaks in the file.
 * This is the one thing Cloud can do that an embedding host usually cannot, because
 * Cloud controls the fonts on both sides.
 *
 * **Export is Cloud's, not the designer's.** `pdfExport: false` and a "Make the
 * document" button of our own, because a PDF rendered in this tab would not carry
 * the free plan's mark, would not be counted against the month, and would not be in
 * the Documents list. The artefact of record is produced by the service.
 *
 * **Saving is automatic and says so.** `onSave` writes, and the indicator beside it
 * says where it went — the designer asks for that sentence precisely because only
 * the host knows the answer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createReportDesigner, type ReportDesignerApi } from "@broadpaper/editor";
import { FormeEditorMeasurer } from "@broadpaper/forme";
import type { ReportTemplate } from "@broadpaper/core";
import { sampleCsv, sampleSheet } from "@broadpaper/mapping";
import { api, waitForReport, type DatasetSummary, type TemplateDetail } from "../api.js";
import { reportFailure } from "../App.js";
import { go } from "../router.js";
import { Button, Chip, Empty, Icon, Note, count, toast } from "../ui/index.js";
import { atLimit, upgradeTarget, useWorkspace } from "../workspace.js";
import { designerTheme } from "./design-theme.js";
import { cloudAiProvider } from "./design-ai.js";

/**
 * The engine, for measuring the canvas.
 *
 * Imported dynamically and initialised once, and both halves of that matter.
 *
 * **Dynamically**, because the WebAssembly module is nearly seven megabytes and a
 * static import would put it in the first chunk the browser downloads — so opening
 * the home page would pay for the designer's engine before painting anything. It is
 * fetched when a designer mounts, which is the only screen that needs it.
 *
 * **Once**, at module scope, because initialising it per mount would pay for it
 * again on every navigation between designs. The demo's `forme-measurer.ts` does
 * the same, for the same reason.
 */
let engine: Promise<typeof import("@formepdf/core/browser")> | null = null;

/**
 * The engine measuring the canvas reads its fonts from the designer's theme, which
 * `designerTheme` fills with Cloud's four families.
 *
 * The browser has them from `main.tsx`'s `@font-face` rules, and the engine does
 * not: it is a separate program with its own font table, and without them it
 * measured every document in its standard faces. The render service embeds the
 * same files, so Preview was measuring text the file never contains — an imported
 * invoice set in Inter measured 65px short, broke its first page 20px early, and a
 * document near a page boundary showed a different page count from the one it
 * produced. The same bytes on both sides is the whole reason the font set is closed.
 * `@broadpaper/forme` fetches each file once and keeps it, and forgets a failure so
 * the next measurement tries again.
 */
function createMeasurer(): FormeEditorMeasurer {
  return new FormeEditorMeasurer({
    render: async (doc) => {
      engine ??= (async () => {
        const module = await import("@formepdf/core/browser");
        await module.init();
        return module;
      })();
      const { renderSerializedDocWithLayout } = await engine;
      return (await renderSerializedDocWithLayout(doc as never)) as never;
    },
    onWarning: (message) => console.warn("[broadpaper]", message)
  });
}

type Save = { state: "clean" } | { state: "saving" } | { state: "saved"; at: Date } | { state: "failed"; message: string };

/**
 * The starter's sample data, saved as a CSV.
 *
 * Built in the tab from what the screen already holds — a design with no data of
 * its own is shown its starter's declaration and sample — so there is nothing to
 * ask the service for. CSV rather than a workbook because every spreadsheet opens
 * one and ingest reads it back without a second parser in this chunk.
 */
function downloadSample(detail: TemplateDetail): void {
  const source = detail.dataSources[0];
  if (!source) return;
  const csv = sampleCsv(sampleSheet(source, detail.data[source.id]));
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${detail.template.name.replace(/[\\/:*?"<>|]+/g, " ").trim() || "Sample"} - sample data.csv`;
  link.click();
  // After the click has been handled, not during it: revoking at once cancels the
  // download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function Design({ id }: { id: string }) {
  const { view, refresh } = useWorkspace();
  const mount = useRef<HTMLDivElement>(null);
  const designer = useRef<ReportDesignerApi | null>(null);
  const [detail, setDetail] = useState<TemplateDetail | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [save, setSave] = useState<Save>({ state: "clean" });
  const [producing, setProducing] = useState<string | null>(null);
  /** Whether the "one file each, or all in one" question is on screen. */
  const [asking, setAsking] = useState(false);
  /** Data already in the workspace, for a design that has none. Loaded only then. */
  const [datasets, setDatasets] = useState<DatasetSummary[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .template(id)
      .then((loaded) => !cancelled && setDetail(loaded))
      .catch((e) => !cancelled && setFailed(e instanceof Error ? e.message : "We couldn't open this design."));
    return () => {
      cancelled = true;
    };
  }, [id]);

  /**
   * Stepping through the documents a split design holds.
   *
   * `setSampleData` rather than a remount, which matters more than it sounds: the
   * designer's own state — the selection, the undo history, the scroll position —
   * survives, so moving from Aldermead's invoice to the next one is looking at
   * another document rather than starting again. The template is untouched; only
   * the data behind the canvas changes.
   */
  /*
   * The workspace's data, fetched only for a design that has none.
   *
   * A design that is already pointed at a spreadsheet has no use for the list, and
   * this screen is the one that has to open quickly.
   */
  useEffect(() => {
    if (!detail || detail.dataset || datasets) return;
    let cancelled = false;
    api
      .datasets()
      .then(({ datasets: loaded }) => !cancelled && setDatasets(loaded))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [detail, datasets]);

  /** Points this design at data the workspace already holds, and goes to match it. */
  const useDataset = async (datasetId: string) => {
    try {
      const template = designer.current?.getTemplate();
      await api.saveTemplate(id, { datasetId, ...(template ? { template } : {}) });
      // A design started from a template has fields to match the columns to. One
      // started from an empty page binds to the columns themselves, and the matching
      // screen refuses it — it used to send somebody there anyway, to an error — so it
      // reloads with the data in its Data panel instead.
      if (detail?.template.starterId) go(`/designs/${id}/match`);
      else setDetail(await api.template(id));
    } catch (e) {
      reportFailure(e, "We couldn't connect that data.");
    }
  };

  /**
   * Changing what separates one document from the next.
   *
   * Saved and reloaded rather than held on screen, because every count on this bar
   * — how many documents, which one of them — is the service's answer and not a
   * guess this screen could make.
   */
  const splitBy = async (groupBy: string | null) => {
    try {
      await api.saveTemplate(id, { groupBy });
      const reloaded = await api.template(id);
      setDetail(reloaded);
      designer.current?.setSampleData(reloaded.data);
      toast(groupBy ? `Split into ${reloaded.groups.length} documents.` : "One document, with every row on it.", "good");
    } catch (e) {
      reportFailure(e, "We couldn't change that.");
    }
  };

  const showGroup = useCallback(
    async (key: string) => {
      try {
        const loaded = await api.template(id, key);
        setDetail(loaded);
        designer.current?.setSampleData(loaded.data);
      } catch (e) {
        reportFailure(e, "We couldn't show that one.");
      }
    },
    [id]
  );

  /**
   * Saving.
   *
   * The designer decides when — it debounces its own changes — so this is just the
   * write and the indicator. A failure says so and stays said: the one thing worse
   * than losing work is being told it was saved when it was not.
   */
  const onSave = useCallback(
    async (template: ReportTemplate) => {
      setSave({ state: "saving" });
      try {
        await api.saveTemplate(id, { template, name: template.name });
        setSave({ state: "saved", at: new Date() });
      } catch (e) {
        setSave({ state: "failed", message: e instanceof Error ? e.message : "Could not save." });
        reportFailure(e, "We couldn't save your changes.");
      }
    },
    [id]
  );

  /*
   * Mounted once per design, not once per anything that changes about it.
   *
   * The dependency used to be `detail`, which is replaced whenever the canvas is
   * pointed at another of the documents a split file holds — so stepping from one
   * invoice to the next destroyed and rebuilt the designer, and with it the
   * selection, the undo history, the scroll position and the mode. Somebody
   * checking six invoices in Preview was put back into Design on every one.
   *
   * The key is what genuinely needs a new designer: a different design, a
   * different letterhead, or different data — which for a design started from an
   * empty page is a different set of fields in the Data panel, and `setSampleData`
   * changes values, not fields. Another document out of the same data is changed in
   * place by `setSampleData`, which is what it is for.
   */
  const mountKey = detail ? `${detail.template.id}:${detail.brand?.id ?? ""}:${detail.dataset?.id ?? ""}` : null;
  const latest = useRef(detail);
  latest.current = detail;

  useEffect(() => {
    const element = mount.current;
    const detail = latest.current;
    if (!element || !detail || !mountKey) return;

    const instance = createReportDesigner(element, {
      template: detail.json,
      dataSources: detail.dataSources,
      sampleData: detail.data,
      theme: designerTheme(detail.theme),
      locale: "en-GB",
      currency: "GBP",
      // The engine, not the DOM. Cloud embeds the fonts the theme names on both
      // sides, so the canvas and the file agree about where a line breaks — which
      // is the trap `CLAUDE.md` documents and the reason the font set is closed.
      createMeasurer,
      features: {
        // Developer affordances, every one of them off.
        expressions: false,
        jsonView: false,
        customBlocks: false,
        library: false,
        // Cloud produces the document itself, so the designer's own export is off:
        // a PDF made in this tab would carry no mark on the free plan, count
        // against nothing, and appear in no list.
        pdfExport: false,
        // The rest stay on. These are what designing a document *is*.
        themes: true,
        styleOverrides: true,
        pageSettings: true,
        documentStyles: true,
        headerFooter: true,
        layers: true,
        dataPanel: true,
        validation: true,
        whiteLabel: true
      },
      onSave,
      saveDescription: "Saved to your workspace as you work.",
      onChange: () => setSave((current) => (current.state === "saving" ? current : { state: "clean" }))
    });
    designer.current = instance;

    // The assistant is added once the service says it is on, rather than holding
    // the designer back while it asks: with AI switched off — or the answer slow —
    // the panel simply never appears.
    let live = true;
    const ai = cloudAiProvider();
    void ai.allowance!().then((allowance) => {
      if (live && allowance) instance.update({ ai });
    });

    return () => {
      live = false;
      instance.destroy();
      designer.current = null;
    };
  }, [mountKey, onSave]);

  const full = atLimit(view, "reportsPerMonth");
  const upgrade = upgradeTarget(view);
  const groupIndex = detail ? detail.groups.findIndex((g) => g.key === detail.group) : -1;
  const several = (detail?.groups.length ?? 0) > 1;

  /**
   * Making the document.
   *
   * Saves first, deliberately: producing a PDF from the last saved version while
   * the user is looking at an edited canvas is the kind of discrepancy that takes
   * an afternoon to understand. Then it queues, waits, and downloads — with the
   * button saying which of those it is doing.
   */
  /**
   * Making the document, or all of them.
   *
   * `scope` is the answer to what a split file should produce. `separate` is what a
   * person sends — three invoices to three customers, each its own file in
   * Documents; `combined` is one file with all of them in it, for printing or
   * filing in one go; `one` is just the document on the canvas. A split design asks
   * before it produces, because three documents is three against the monthly count
   * and that is not a thing to discover afterwards.
   *
   * Only the first file is downloaded when several are made. The browser can be
   * given one download per navigation, and the rest are in Documents a click away —
   * which is also where somebody making twelve invoices wants them.
   */
  const produce = async (scope: "separate" | "combined" | "one") => {
    const instance = designer.current;
    if (!instance) return;
    setAsking(false);
    setProducing("Saving your design…");
    try {
      await api.saveTemplate(id, { template: instance.getTemplate(), name: instance.getTemplate().name });
      setProducing(scope === "separate" && several ? `Producing ${detail!.groups.length} documents…` : "Producing the document…");
      const { reports } = await api.createReport({
        templateId: id,
        scope,
        ...(scope === "one" && detail?.group ? { group: detail.group } : {})
      });

      const finished = [];
      for (const queued of reports) {
        finished.push(
          await waitForReport(queued.id, (r) =>
            setProducing(
              reports.length > 1
                ? `Producing ${finished.length + 1} of ${reports.length}…`
                : r.status === "rendering" && r.parts && r.parts > 1
                  ? // Said as soon as the length is estimated, because a document of
                    // several files takes minutes and nobody should wonder whether
                    // the button worked.
                    `Producing a long document — about ${count(r.parts)} files of up to 250 pages each…`
                  : r.status === "rendering"
                    ? "Producing the document…"
                    : "In the queue…"
            )
          )
        );
      }

      const failures = finished.filter((r) => r.status === "failed");
      const ready = finished.filter((r) => r.status === "ready");
      setProducing(null);
      await refresh();

      if (failures.length) {
        toast(failures[0]!.error ?? "We couldn't produce that document.", "bad");
        if (!ready.length) return;
      }

      const { url } = await api.downloadReport(ready[0]!.id);
      // A real navigation, because it is a download and the browser should treat
      // it as one.
      window.location.href = url;
      const files = ready.length === 1 ? (ready[0]!.parts ?? 1) : ready.length;
      toast(
        ready.length === 1 && files > 1
          ? `A long document, made as ${count(files)} files of up to 250 pages. The first is downloading; the rest are in your Documents.`
          : ready.length === 1
            ? `${ready[0]!.pages} page${ready[0]!.pages === 1 ? "" : "s"} — it is in your Documents too.`
            : `${ready.length} documents made. The first is downloading; the rest are in your Documents.`,
        "good"
      );
    } catch (e) {
      setProducing(null);
      reportFailure(e, "We couldn't produce that document.");
    }
  };

  /**
   * Changing which letterhead this design carries.
   *
   * The canvas is saved first and the detail reloaded after, which remounts the
   * designer with the new theme. Saving first is not optional: the remount takes
   * the canvas's own state with it, and losing somebody's unsaved layout because
   * they changed the address block would be unforgivable.
   */
  const switchBrand = async (brandId: string) => {
    const instance = designer.current;
    try {
      await api.saveTemplate(id, { ...(instance ? { template: instance.getTemplate() } : {}), brandId });
      setDetail(await api.template(id));
      toast("This design now uses those details.", "good");
    } catch (e) {
      reportFailure(e, "We couldn't change that.");
    }
  };

  /*
   * Above the early return, and it has to stay there. A hook after a conditional
   * `return` changes the hook count the moment the condition flips — here, the
   * moment a design fails to load — which React reports as a different component
   * having been rendered.
   */
  const status = useMemo(() => {
    if (save.state === "saving") return <span className="row small muted" style={{ gap: 6 }}><span className="spinner" /> Saving…</span>;
    if (save.state === "saved") return <span className="row small muted" style={{ gap: 6 }}><Icon name="check" /> Saved</span>;
    if (save.state === "failed") return <span className="row small" style={{ gap: 6, color: "var(--red-deep)" }}><Icon name="warn" /> Not saved</span>;
    return <span className="small muted">Saves as you work</span>;
  }, [save]);

  if (failed) {
    return (
      <main className="main">
        <Empty title="We couldn't open this design" actions={<a className="btn btn--primary" href="/designs">Back to your designs</a>}>
          {failed}
        </Empty>
      </main>
    );
  }

  return (
    <>
      <div className="designer__bar">
        <a className="btn btn--quiet btn--sm" href="/designs">
          <Icon name="back" /> Designs
        </a>
        <strong className="truncate" style={{ maxWidth: 260 }}>
          {detail?.template.name ?? "…"}
        </strong>
        {detail?.dataset ? (
          <Chip tone="good">{detail.dataset.name}</Chip>
        ) : detail ? (
          <Chip tone="ask">No data yet</Chip>
        ) : null}
        {/*
          Whose letterhead this design carries.
          
          In the bar rather than buried in a panel because it is a property of the
          document a person needs to see at a glance — an invoice sent under the
          wrong trading name is not a small mistake. A picker when there is
          something to pick between, a link to go and fill it in when there is not.
        */}
        {detail && detail.brands.length > 1 ? (
          <select
            className="input"
            style={{ height: 30, maxWidth: 190, fontSize: 13 }}
            value={detail.brand?.id ?? ""}
            onChange={(e) => void switchBrand(e.target.value)}
            aria-label="Which of your business details this design uses"
            title="Which of your business details this design uses"
          >
            {detail.brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name}
              </option>
            ))}
          </select>
        ) : detail?.brand ? (
          <a className="btn btn--quiet btn--sm" href="/brand" title="Your name, address and logo, as they appear on this document">
            {detail.brand.name}
          </a>
        ) : detail ? (
          <a className="btn btn--quiet btn--sm" href="/brand">
            Add your letterhead
          </a>
        ) : null}
        {/*
          Which of the documents this file holds is on the canvas.
          
          The answer to "you cannot see what the output looks like for each row":
          previous, next, and a name, so a design split by customer can be checked
          customer by customer before anything is produced.
        */}
        {/*
          What the file is split by, beside the stepper that shows the effect.
          
          The fields screen has always had this and it is the wrong place to find
          out: somebody sees six documents here, realises the wrong column was
          chosen, and needs to fix it here rather than be sent looking.
        */}
        {detail && (detail.groupBy || detail.splitOptions.length) ? (
          <select
            className="input"
            style={{ height: 30, maxWidth: 190, fontSize: 13 }}
            value={detail.groupBy ?? ""}
            onChange={(e) => void splitBy(e.target.value || null)}
            aria-label="What separates one document from the next"
            title="What separates one document from the next"
          >
            <option value="">One document, every row</option>
            {detail.splitOptions.map((o) => (
              <option key={o.column} value={o.column}>
                One per {o.label.split(" › ").pop()!.toLowerCase()} ({o.groups})
              </option>
            ))}
            {detail.groupBy && !detail.splitOptions.some((o) => o.column === detail.groupBy) ? (
              <option value={detail.groupBy}>Split by “{detail.groupBy}”</option>
            ) : null}
          </select>
        ) : null}
        {detail && detail.groups.length > 1 ? (
          <div className="row" style={{ gap: 4, alignItems: "center" }}>
            <Button
              size="sm"
              variant="quiet"
              icon="back"
              title="The one before"
              disabled={groupIndex <= 0}
              onClick={() => void showGroup(detail.groups[groupIndex - 1]!.key)}
            />
            <select
              className="input"
              style={{ height: 30, maxWidth: 200, fontSize: 13 }}
              value={detail.group ?? ""}
              onChange={(e) => void showGroup(e.target.value)}
              aria-label="Which document to show"
            >
              {detail.groups.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.label} · {g.rows} row{g.rows === 1 ? "" : "s"}
                </option>
              ))}
            </select>
            <span className="small muted" style={{ whiteSpace: "nowrap" }}>
              {groupIndex + 1} of {detail.groups.length}
            </span>
            <Button
              size="sm"
              variant="quiet"
              icon="chevron"
              title="The next one"
              disabled={groupIndex < 0 || groupIndex >= detail.groups.length - 1}
              onClick={() => void showGroup(detail.groups[groupIndex + 1]!.key)}
            />
          </div>
        ) : null}
        <div className="grow" />
        {status}
        {/* Named for the question somebody actually has. "Check the fields" read as
            an optional chore; this reads as the answer to "why does it say that",
            which is when people come looking. */}
        {/*
          History, saving first.

          The designer saves on its own a moment after each change, so a click
          straight after an edit could reach the history before that edit had — and
          the newest version on the next screen would be missing the thing somebody
          just did.
        */}
        {detail ? (
          <a
            className="btn btn--quiet btn--sm"
            href={`/designs/${id}/history`}
            title="Earlier versions of this design, and what changed in each"
            onClick={async (event) => {
              event.preventDefault();
              const template = designer.current?.getTemplate();
              if (template) await api.saveTemplate(id, { template, name: template.name }).catch(() => undefined);
              go(`/designs/${id}/history`);
            }}
          >
            <Icon name="history" /> History
          </a>
        ) : null}
        {/* Only a design with a template's fields has anything to match. */}
        {detail?.dataset && detail.template.starterId ? (
          <a className="btn btn--sm" href={`/designs/${id}/match`} title="See and change what fills each field on this document">
            <Icon name="data" /> Fields
          </a>
        ) : null}
        {/*
          The figures themselves, from the design they are wrong on.

          Saving first, like History: the designer saves a moment after each change,
          and the grid is a whole screen away from it.
        */}
        {detail?.dataset ? (
          <a
            className="btn btn--sm"
            href={`/data/${detail.dataset.id}/edit?design=${id}`}
            title={`Change the rows in “${detail.dataset.name}”`}
            onClick={async (event) => {
              event.preventDefault();
              const template = designer.current?.getTemplate();
              if (template) await api.saveTemplate(id, { template, name: template.name }).catch(() => undefined);
              go(`/data/${detail.dataset!.id}/edit?design=${id}`);
            }}
          >
            <Icon name="pencil" /> Edit data
          </a>
        ) : null}
        <Button
          size="sm"
          variant="primary"
          icon="download"
          // A split design asks first. Three documents is three against the
          // monthly count, and that is not a thing to find out afterwards.
          onClick={() => (several ? setAsking(true) : void produce("one"))}
          busy={producing !== null}
          disabled={!detail || !detail.dataset || full}
          title={full ? "You have used this month's documents" : undefined}
        >
          {producing ?? (several ? `Make ${detail!.groups.length} documents` : "Make the document")}
        </Button>
      </div>

      {/*
        Three answers, as a list.
        
        It was four buttons in a dialog's footer — Cancel, All in one file, Just
        "INV-10248", 6 separate files — which ran off the end of a 460px sheet and
        read as one question with three afterthoughts. It is three choices, each
        with what it costs against the month, and a cancel.
      */}
      {asking && detail ? (
        <div className="scrim" onPointerDown={(e) => e.target === e.currentTarget && setAsking(false)}>
          <div className="sheet" role="dialog" aria-modal="true" aria-label="What should BroadPaper produce?">
            <div className="card__body stack" style={{ gap: 14 }}>
              <div className="stack" style={{ gap: 4 }}>
                <h3>Your data holds {detail.groups.length} documents</h3>
                <p className="muted">
                  You have {count(Math.max(0, view.limits.reportsPerMonth - view.usage.reportsThisMonth))} of this month's{" "}
                  {count(view.limits.reportsPerMonth)} left.
                </p>
              </div>

              <button type="button" className="tile" style={{ padding: "12px 14px" }} onClick={() => void produce("separate")}>
                <div className="spread">
                  <div className="stack" style={{ gap: 2 }}>
                    <strong style={{ fontSize: 15 }}>{count(detail.groups.length)} separate files</strong>
                    <span className="small muted">One per document, each named after it. This is what you send.</span>
                  </div>
                  <span className="small muted" style={{ whiteSpace: "nowrap" }}>
                    uses {count(detail.groups.length)}
                  </span>
                </div>
              </button>

              <button type="button" className="tile" style={{ padding: "12px 14px" }} onClick={() => void produce("combined")}>
                <div className="spread">
                  <div className="stack" style={{ gap: 2 }}>
                    <strong style={{ fontSize: 15 }}>All in one file</strong>
                    <span className="small muted">Every document in a single PDF, for printing or filing in one go.</span>
                  </div>
                  <span className="small muted" style={{ whiteSpace: "nowrap" }}>uses 1</span>
                </div>
              </button>

              <button type="button" className="tile" style={{ padding: "12px 14px" }} onClick={() => void produce("one")}>
                <div className="spread">
                  <div className="stack" style={{ gap: 2 }}>
                    <strong style={{ fontSize: 15 }}>Just “{detail.groups.find((g) => g.key === detail.group)?.label ?? "this one"}”</strong>
                    <span className="small muted">Only the document on screen.</span>
                  </div>
                  <span className="small muted" style={{ whiteSpace: "nowrap" }}>uses 1</span>
                </div>
              </button>
            </div>
            <div className="card__foot row" style={{ justifyContent: "flex-end" }}>
              <button type="button" className="btn" onClick={() => setAsking(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/*
        A saved choice the service has stopped honouring.
        
        This is the whole answer to "where did that number come from": the field, the
        column, why it is not being used, and one click to the screen that fixes it.
        Before this the document simply showed 45,000% and there was nothing on
        screen — here or anywhere — that named the cause.
      */}
      {detail?.dropped.length ? (
        <div style={{ padding: "14px var(--gutter)", borderBottom: "1px solid var(--rule)" }}>
          <Note tone="ask" title={detail.dropped.length === 1 ? "One field is not being filled in" : `${detail.dropped.length} fields are not being filled in`}>
            {detail.dropped.map((d) => (
              <span key={d.path} style={{ display: "block" }}>
                <strong>{d.label.split(" › ").pop()}</strong> was set to your “{d.column}” column. {d.because} BroadPaper has left it out rather than print
                a figure it does not believe.
              </span>
            ))}
            <a href={`/designs/${id}/match`}>Choose what fills it →</a>
          </Note>
        </div>
      ) : null}

      {/*
        A spreadsheet that holds more than one document.
        
        The most dangerous shape in the product, because it maps perfectly: three
        customers in one file gives one invoice with the first customer's name above
        everybody's lines, nothing unmapped and nothing to see. BroadPaper cannot
        split it yet, so the least it can do is say which name it is using and how
        many it is ignoring.
      */}
      {detail?.firstRowOnly.length ? (
        <div style={{ padding: "14px var(--gutter)", borderBottom: "1px solid var(--rule)" }}>
          <Note tone="ask" title="Your data may hold more than one document">
            {detail.firstRowOnly.map((f) => (
              <span key={f.path} style={{ display: "block" }}>
                <strong>{f.label.split(" › ").pop()}</strong> has {f.distinct} different values in your data. This document uses{" "}
                <strong>{String(f.using)}</strong> and every row is on it.
              </span>
            ))}
            If each one should be its own document, bring in one set of data per document for now — or{" "}
            <a href={`/designs/${id}/match`}>choose a single value</a> to put on this one.
          </Note>
        </div>
      ) : null}

      {/*
        A design with no data of its own, and the three ways out of it.
        
        The first of them was missing: a design could only be pointed at data by
        importing a spreadsheet *again*, even when the right one was already in the
        workspace. The list is loaded here rather than on the Designs screen
        because this is where somebody realises they wanted it.
      */}
      {detail && !detail.dataset ? (
        <div style={{ padding: "14px var(--gutter)", borderBottom: "1px solid var(--rule)" }}>
          <Note tone="info" title={detail.template.starterId ? "This design is using sample data" : "This design has no data yet"}>
            <div className="stack" style={{ gap: 10 }}>
              <span>
                {detail.template.starterId
                  ? "It looks finished because it comes with example figures. Point it at your own and BroadPaper fills it in from that instead."
                  : "Give it some and your columns appear in the Data panel, ready to drag onto the page."}
              </span>
              <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                {datasets?.length ? (
                  <>
                    <select
                      className="input"
                      style={{ maxWidth: 260, height: 34 }}
                      value=""
                      onChange={(e) => e.target.value && void useDataset(e.target.value)}
                      aria-label="Data you have already brought in"
                    >
                      <option value="">Use data you already have…</option>
                      {datasets.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name} · {count(d.rowCount)} rows
                        </option>
                      ))}
                    </select>
                    <span className="small muted">or</span>
                  </>
                ) : null}
                <a className="btn btn--sm" href="/data/new">
                  <Icon name="upload" /> Bring in a spreadsheet
                </a>
                {detail.template.starterId ? (
                  <>
                    <span className="small muted">or</span>
                    <a className="btn btn--sm" href={`/designs/${id}/fill`}>
                      <Icon name="pencil" /> Type the details in
                    </a>
                    {/*
                      The shape the data has to be, as a file rather than a
                      sentence. It is this document's own figures laid out the way
                      an upload is read — one row per line, the single values
                      repeated, headed with the fields' names — so bringing it
                      straight back in gives this page, and replacing its rows gives
                      somebody's own. `starters.test.ts` holds every starter to that.
                    */}
                    <Button size="sm" variant="quiet" icon="download" onClick={() => downloadSample(detail)}>
                      Download a sample spreadsheet
                    </Button>
                  </>
                ) : (
                  // No template's fields to fill in, so the columns are the person's own.
                  <>
                    <span className="small muted">or</span>
                    <a className="btn btn--sm" href={`/data/new/table?design=${id}`}>
                      <Icon name="pencil" /> Type your data in
                    </a>
                  </>
                )}
              </div>
            </div>
          </Note>
        </div>
      ) : null}

      {full ? (
        <div style={{ padding: "14px var(--gutter)", borderBottom: "1px solid var(--rule)" }}>
          <Note tone="ask" title={`You have produced all ${view.limits.reportsPerMonth} documents your plan includes this month`}>
            The count resets on the first of the month{upgrade ? `, or move to ${upgrade.name} for ${upgrade.limits.reportsPerMonth.toLocaleString("en-GB")} a month.` : "."}{" "}
            {upgrade ? <a href="/account">See {upgrade.name} →</a> : null}
          </Note>
        </div>
      ) : null}

      <div className="designer">
        <div ref={mount} className="designer__mount" />
      </div>
    </>
  );
}
